/**
 * Debug utilities for tracking React behavior and API call patterns
 */

// Track component mount counts to detect React StrictMode double renders
const componentMountCounts = new Map<string, number>();

export const trackComponentMount = (componentName: string): number => {
  const currentCount = componentMountCounts.get(componentName) || 0;
  const newCount = currentCount + 1;
  componentMountCounts.set(componentName, newCount);
  
  if (newCount > 1) {
    console.warn(`${componentName} mounted ${newCount} times - potential React StrictMode double render`);
  }
  
  return newCount;
};

// Track API calls to detect excessive requests
const apiCallCounts = new Map<string, { count: number; lastCall: number }>();

export const trackApiCall = (endpoint: string): void => {
  const now = Date.now();
  const existing = apiCallCounts.get(endpoint);
  
  if (existing) {
    const timeSinceLastCall = now - existing.lastCall;
    existing.count++;
    existing.lastCall = now;
    
    // Warn about rapid successive calls (within 1 second)
    if (timeSinceLastCall < 1000) {
      console.warn(`Rapid API call to ${endpoint} (${existing.count} calls, ${timeSinceLastCall}ms since last)`);
    }
  } else {
    apiCallCounts.set(endpoint, { count: 1, lastCall: now });
  }
};

// Get API call statistics for debugging
export const getApiCallStats = (): Record<string, { count: number; lastCall: number }> => {
  const stats: Record<string, { count: number; lastCall: number }> = {};
  
  for (const [endpoint, data] of apiCallCounts.entries()) {
    stats[endpoint] = { ...data };
  }
  
  return stats;
};

// Reset tracking data (useful for testing)
export const resetDebugTracking = (): void => {
  componentMountCounts.clear();
  apiCallCounts.clear();
};

// Log a summary of potential issues
export const logDebugSummary = (): void => {
  console.group('Debug Summary - Potential Performance Issues');
  
  // Component mount issues
  const multiMountComponents = Array.from(componentMountCounts.entries())
    .filter(([, count]) => count > 1);
  
  if (multiMountComponents.length > 0) {
    console.warn('Components mounted multiple times (React StrictMode?):');
    multiMountComponents.forEach(([component, count]) => {
      console.warn(`  ${component}: ${count} mounts`);
    });
  }
  
  // API call frequency issues
  const frequentApiCalls = Array.from(apiCallCounts.entries())
    .filter(([, data]) => data.count > 2);
  
  if (frequentApiCalls.length > 0) {
    console.warn('Frequent API calls detected:');
    frequentApiCalls.forEach(([endpoint, data]) => {
      console.warn(`  ${endpoint}: ${data.count} calls`);
    });
  }
  
  console.groupEnd();
};